"use client";

import clsx from "clsx";
import React from "react";
import {useTranslation} from "react-i18next";
import {type ArtifactData, useArtifactPanel, useAutoOpenArtifact} from "@/contexts/chat/ArtifactPanelContext";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import type {MessageContentScriptDraft} from "@/services/wingman";
import Icon, {ICON_SIZES} from "../library/Icon";
import FileBubbleFrame from "./ask-file-bubble/FileBubbleFrame";

/**
 * Chat card for a script draft proposed by the assistant. The card is a
 * compact summary; clicking it (or its arrival, via auto-open) shows the full
 * draft in the workspace panel. Modeled on `AskGeneratedFileBubble`.
 */
const AskScriptDraftBubble: React.FC<{
   isUser?: boolean;
   agentName?: string;
   brandOverride?: string;
   className?: string;
   scriptDraft: MessageContentScriptDraft;
   isLastInGroup?: boolean;
   autoOpen?: boolean;
   hideAgentHeader?: boolean;
   feedbackSlot?: React.ReactNode;
   copyText?: string;
}> = ({
   isUser,
   agentName,
   brandOverride,
   className,
   scriptDraft,
   isLastInGroup = true,
   autoOpen = false,
   hideAgentHeader = false,
   feedbackSlot,
   copyText,
}) => {
   const {t} = useTranslation("ask");
   const {openArtifact} = useArtifactPanel();
   const {isDockAvailable} = useGailDock();

   const {draft, callId} = scriptDraft;

   const buildArtifact = (): ArtifactData => ({
      kind: "script_draft",
      id: callId,
      draft,
   });

   const openInPanel = () => openArtifact(buildArtifact());

   const opensWorkspace = !isDockAvailable;

   useAutoOpenArtifact(autoOpen && opensWorkspace, callId, buildArtifact, openArtifact);

   const handleCardKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
         e.preventDefault();
         openInPanel();
      }
   };

   const interactiveProps: React.HTMLAttributes<HTMLDivElement> = opensWorkspace
      ? {
           role: "button",
           tabIndex: 0,
           onClick: openInPanel,
           onKeyDown: handleCardKeyDown,
           "aria-label": t("workspace.scriptDraft.a11yOpen"),
        }
      : {};

   return (
      <FileBubbleFrame
         isUser={isUser}
         agentName={agentName}
         brandOverride={brandOverride}
         className={className}
         isLastInGroup={isLastInGroup}
         hideAgentHeader={hideAgentHeader}
         feedbackSlot={feedbackSlot}
         copyText={copyText}
      >
         <div
            {...interactiveProps}
            className={clsx(
               "group relative block overflow-hidden rounded-xl border border-300",
               "bg-panel w-full max-w-[620px]",
               opensWorkspace &&
                  "cursor-pointer transition-all hover:border-primary/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            )}
         >
            <div className="flex items-center gap-3 px-4 py-3">
               <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                  <Icon icon="file-lines" size={ICON_SIZES.SM} />
               </div>
               <div className="flex-1 min-w-0">
                  <p className="font-medium text-900 text-sm truncate" title={draft.scriptName}>
                     {draft.scriptName}
                  </p>
                  <p className="text-[10px] text-500 uppercase tracking-wide mt-0.5">
                     {t("workspace.scriptDraft.badge")}
                  </p>
               </div>
               {opensWorkspace && (
                  <Icon
                     icon="arrow-up-right-from-square"
                     size={ICON_SIZES.SM}
                     className="text-500 group-hover:text-primary transition-colors flex-shrink-0"
                  />
               )}
            </div>
         </div>
      </FileBubbleFrame>
   );
};

export default AskScriptDraftBubble;
